import { Lightning } from '@phosphor-icons/react';
import { memo } from 'react';

import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import type { ProductListing } from '../../lib/productVariants';
import { stockLabel } from '../../lib/stockLabel';
import { ProductLogo } from '../ProductLogo';
import { DealCountdown } from '../DealCountdown';
import styles from './ProductCard.module.css';

interface ProductCardProps {
  listing: ProductListing;
  onSelect: (listing: ProductListing) => void;
  /** Skips the product view and opens the sheet on its confirmation step. */
  onBuy: (listing: ProductListing) => void;
  /** Set on the tiles that start on screen; the rest defer their logo. */
  eager?: boolean;
  clockOffsetMs?: number;
}

/**
 * Memoised because the whole grid re-renders on every keystroke in the search
 * box. The props are a stable product object and two callbacks the page holds
 * in useCallback, so a card only re-renders when its own product changes.
 */
export const ProductCard = memo(function ProductCard({
  listing,
  onSelect,
  onBuy,
  eager = false,
  clockOffsetMs = 0,
}: ProductCardProps) {
  const { primary: product, variants } = listing;
  const hasVariants = variants.length > 1;
  const popular = variants.some((variant) => variant.product.popular);
  const availableVariants = variants.filter((variant) => variant.product.inStock);
  const hasAvailableVariant = availableVariants.length > 0;
  const priceCandidates = availableVariants.length > 0 ? availableVariants : variants;
  const lowestPriced = priceCandidates.reduce(
    (lowest, variant) =>
      Number(variant.product.price.amount) < Number(lowest.product.price.amount) ? variant : lowest,
    priceCandidates[0]!,
  ).product;

  return (
    <Card className={styles.card} onClick={() => onSelect(listing)}>
      {product.exclusive || popular ? (
        <svg
          className={[styles.ribbon, product.exclusive ? styles.exclusiveRibbon : '']
            .filter(Boolean)
            .join(' ')}
          viewBox="0 0 84 84"
          role="img"
          aria-label={product.exclusive ? 'Exclusive' : 'Popular'}
          focusable="false"
        >
          {/* Matching outer tabs make the sash appear to pass over the card's
              top and right edges instead of ending at either boundary. */}
          <path className={styles.ribbonFace} d="M17 0H37L43 6 78 41 84 47V67L78 61 23 6Z" />
          <path className={styles.ribbonFold} d="M17 0 23 6H17Z" />
          <path className={styles.ribbonFold} d="M78 61 84 67H78Z" />
          <text
            className={styles.ribbonLabel}
            x="55.5"
            y="28.5"
            textAnchor="middle"
            dominantBaseline="central"
            transform="rotate(45 55.5 28.5)"
          >
            {product.exclusive ? 'EXCLUSIVE' : 'POPULAR'}
          </text>
        </svg>
      ) : null}

      <div className={styles.media}>
        <ProductLogo
          className={styles.logo}
          src={product.logoUrl}
          // Lazy-loading the tiles that are already on screen delays the one
          // thing the page is mostly made of; the first row loads immediately
          // and everything below it waits until it is scrolled towards.
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          width={96}
          height={96}
          iconSize={40}
        />

        {/* The discount sits on the artwork rather than in the text block: it
            costs the card no height there, and lands where the eye already is
            on a grid of images. */}
        {product.exclusive ? (
          <span className={styles.limitedBadge}>{product.discountLabel ?? 'LIMITED OFFER'}</span>
        ) : product.flashDeal ? (
          <span className={styles.flashBadge}>
            <Lightning size={10} weight="fill" />
            <span>FLASH</span>
            <DealCountdown
              endsAt={product.flashDeal.endsAt}
              clockOffsetMs={clockOffsetMs}
              compact
            />
          </span>
        ) : product.discountLabel ? (
          <span className={styles.discountBadge}>{product.discountLabel}</span>
        ) : null}
      </div>

      <div className={styles.body}>
        {/* One line, truncated. Names run from "Canva Pro" to "3 Month Spotify
            Premium Subscription", and reserving two lines for the long ones
            left an empty second line on every short one — which was most of
            the wasted height in the tile. */}
        <p className={styles.name} title={listing.name}>
          {listing.name}
        </p>

        <p className={hasAvailableVariant ? styles.stock : styles.stockOut}>
          {hasVariants
            ? hasAvailableVariant
              ? `${availableVariants.length} ${availableVariants.length === 1 ? 'option' : 'options'} available`
              : 'Out of stock'
            : stockLabel(product)}
        </p>

        {/*
          The struck-out price gets a line that is reserved whether or not it
          is filled. Two tiles side by side then put their real price on the
          same baseline, instead of the discounted one growing a line taller
          than its neighbour. A single row cannot hold both prices at this
          width — 'ETB' is spelled out — so laying them side by side would
          wrap and reintroduce the same ragged edge.
        */}
        <div className={styles.priceBlock}>
          {!hasVariants && product.value ? (
            <span className={styles.value}>Value: {product.value.label}</span>
          ) : null}
          <span className={styles.wasSlot}>
            {!hasVariants && product.listPrice ? (
              <s className={styles.wasPrice}>{product.listPrice.label}</s>
            ) : null}
          </span>
          <span className={styles.price}>
            {hasVariants ? `From ${lowestPriced.price.label}` : product.price.label}
          </span>
        </div>

        {/* The card is the whole product view for most customers, so the
            purchase affordance belongs here. It opens the same confirmation
            the sheet does — nothing is bought from the grid in one tap. */}
        <Button
          className={[styles.buy, popular && hasAvailableVariant ? styles.popularBuy : '']
            .filter(Boolean)
            .join(' ')}
          fullWidth
          variant={hasAvailableVariant ? 'primary' : 'outline'}
          aria-label={
            hasAvailableVariant
              ? hasVariants
                ? `Choose a ${listing.name} option`
                : `Buy ${product.name}`
              : `View information for out-of-stock product ${listing.name}`
          }
          onClick={(event) => {
            // Otherwise the Card underneath also fires and opens the product
            // view on top of the confirmation.
            event.stopPropagation();
            if (hasVariants || !hasAvailableVariant) onSelect(listing);
            else onBuy(listing);
          }}
        >
          <span className={styles.buyLabel}>
            {hasVariants && hasAvailableVariant
              ? 'Choose'
              : hasAvailableVariant
                ? 'Buy'
                : 'Out of stock'}
          </span>
          {popular && hasAvailableVariant ? (
            <span className={styles.fireSparks} aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
              <span />
            </span>
          ) : null}
        </Button>
      </div>
    </Card>
  );
});
